import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Card,
  DialogContent,
  DialogProps,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import Paper, { PaperProps } from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import uuidv4 from 'src/utils/uuidv4';

import { DialogAnimate } from './animate';
import Scrollbar from './scrollbar';

interface DraggablePaperProps extends PaperProps {
  keyId: string;
}

const DraggablePaper = ({ keyId, ...props }: DraggablePaperProps) => {
  const paperRef = useRef<HTMLDivElement>(null);

  return (
    <Draggable
      nodeRef={paperRef}
      handle={'#draggable-dialog-title-' + keyId}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        ref={paperRef}
        id="draggable-paper"
        sx={{
          borderBottomRightRadius: '0 !important',
          '.MuiPaper-rounded': {
            borderRadius: '0',
          },
        }}
        {...props}
      />
    </Draggable>
  );
};

export interface CDialogProps extends DialogProps {
  onClose?: VoidFunction;
  titleIcon?: string | JSX.Element;
  /** **제목** */
  dialogTitle: string;
  dialogSubTitle?: string;
  /** **내용** */
  children: React.ReactNode;
  minHeight?: string;
  height?: string | number;
  isOverflow?: boolean;
  /** 초기 모달 크기 */
  initialHeight?: number;
  /** **모달창 닫기 함수** */
  handleDialogClose: () => void;
  /** **스크롤 여부** */
  hasScroll?: boolean;
  /** **액션** */
  actionButtons?: React.ReactNode;
  /** TOP 액션 */
  topActionButtons?: React.ReactNode;
  /** full Width */
  fullWidth?: boolean;
  /** **모달 top 고정 ** */
  fixedTop?: boolean;
  /** **모달 KEY UUID ** */
  keyId?: string;
  /** **body scrollbar 활성화 여부 ** */
  enableScrollbar?: boolean;
  isNotAnimate?: boolean;
  buttonList?:
    | {
        className: string;
        buttonText: string;
        clickEvent: any;
      }[]
    | JSX.Element[];
  onScroll?: (e: any) => void;
  contentBoxSx?: PaperProps['sx'];
  onClickBackground?: () => void;
  /** **footer 버튼 이벤트 ** */
  footer?: boolean;
  /** **footer 로고 ** */
  logofooter?: boolean;
  /** **dim 처리 활성화 여부 ** */
  dimStyle?: boolean;
}

/**
 * ## 기본 사용법
 *
 * > 모달창 컴포넌트로, 화면 위에 추가적인 창을 띄울 때 사용합니다.
 *
 * > 기본 구성은 모달 제목, 상단 닫기 버튼, 모달 내용 컴포넌트이며, 하위 버튼셋을 선택적으로 추가할 수 있습니다.
 *
 * > 상세보기, 등록, 수정 기능 구현 시 주로 사용합니다.
 *
 */

const CDialog = memo(
  observer(function CDialog({
    open,
    buttonList,
    titleIcon,
    dialogTitle,
    dialogSubTitle,
    maxWidth,
    minHeight = '250px',
    height,
    isOverflow = true, // enableScrollbar === false일 때 사용가능합니다.
    handleDialogClose,
    initialHeight = window.innerHeight * 0.8,
    hasScroll = true,
    children,
    topActionButtons,
    actionButtons,
    fullWidth = false,
    fixedTop = false,
    keyId = uuidv4(),
    enableScrollbar = true,
    isNotAnimate = true,
    onScroll = (e: any) => undefined,
    footer = false,
    contentBoxSx,
    onClickBackground,
    logofooter,
    dimStyle,
    ...other
  }: CDialogProps) {
    const theme = useTheme();
    const [fullScreen, setFullScreen] = useState(false);
    const scrollContentRef = useRef<any>(null);
    const maxWidthPx = useMemo(() => {
      switch (maxWidth) {
        case 'xs':
          return 400;
        case 'sm':
          return 600;
        case 'md':
          return 900;
        case 'lg':
          return 1200;
        case 'xl':
          return 1500;
        default:
          return 400;
      }
    }, [maxWidth]);

    const dialogWidth = maxWidthPx;
    const dialogHeight = Number(height) || initialHeight;

    const onClickBackgroundHandler = () => {
      if (isNotAnimate) {
        onClickBackground && onClickBackground();
      } else {
        handleDialogClose();
      }
    };

    useEffect(() => {
      if (!scrollContentRef.current) {
        return;
      }

      scrollContentRef.current.getScrollElement().addEventListener('scroll', onScroll, false);

      return () => {
        scrollContentRef.current?.getScrollElement().removeEventListener('scroll', onScroll, false);
      };
    }, [onScroll, scrollContentRef]);

    return (
      <DialogAnimate
        open={open}
        onClose={onClickBackgroundHandler}
        PaperComponent={(props) => <DraggablePaper keyId={keyId} {...props} />}
        id="CDialog"
        slotProps={{
          backdrop: {
            invisible: dimStyle ? true : false,
          },
        }}
        PaperProps={{
          keyId: keyId,
          id: 'Paper_' + keyId,
          sx: {
            resize: 'both',
            minHeight: minHeight,
            maxWidth: '100%',
            maxHeight: '100%',
            m: 0,
            width: fullScreen ? '100%' : dialogWidth + 'px',
            height: fullScreen ? '100%' : dialogHeight ? dialogHeight + 'px' : 'auto',
            border: 1,
            borderColor: theme.palette.divider,
            boxShadow: dimStyle ? '3px 3px 15px rgba(0, 0, 0, 0.2)' : 'none',
          },
        }}
        sx={{
          '.MuiDialog-container': { mt: 0, alignItems: 'center' },
        }}
        {...other}
      >
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            height: '100%',
            minHeight: minHeight,
            minWidth: (dialogWidth * 1) / 3,
            background: theme.palette.background.paper,
          }}
        >
          <Stack
            id={'draggable-dialog-title-' + keyId}
            onDoubleClick={() => setFullScreen(!fullScreen)}
            sx={{
              m: 0,
              pt: 0.5,
              pl: 2,
              pr: 1,
              cursor: 'pointer',
            }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {/* 좌측 제목 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  display: 'flex',
                  m: 0,
                  color: theme.palette.text.primary,
                  alignItems: 'center',
                  gridGap: '8px',
                  whiteSpace: 'nowrap',
                  wordBreak: 'keep-all',
                }}
              >
                {titleIcon}
                {dialogTitle}
              </Typography>
            </Box>

            {dialogSubTitle && (
              <Box
                sx={{
                  border: '1px solid #fff',
                  padding: '5px 15px',
                  borderRadius: '10px',
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Typography variant="h6" color={theme.palette.secondary.main}>
                  <pre>{dialogSubTitle}</pre>
                </Typography>
              </Box>
            )}
            {topActionButtons}
            <IconButton onTouchEnd={handleDialogClose} onClick={handleDialogClose} sx={{ p: 0.5 }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ margin: '0 !important' }}></Divider>

          <DialogContent
            sx={{
              p: 0,
              // flex: 1,
              overflow: enableScrollbar === false ? (isOverflow ? `auto` : 'hidden') : 'hidden',
              height: '100%',
              '.MuiBox-root.modalButton': {
                width: '100%',
                position: 'fixed',
                bottom: 0,
                left: 0,
                padding: 4,
                zIndex: 10,
                pt: 0,
                m: 0,
              },
            }}
          >
            {enableScrollbar ? (
              <Scrollbar
                sx={{
                  height: '100%',
                  p: 0,
                  '& .simplebar-content': {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'flex-start',
                  },
                  ':has(.modalButton)': {
                    height: 'calc(100% - 70px)',
                  },
                }}
                ref={scrollContentRef}
              >
                <Box sx={{ p: 2, pb: 2, ...contentBoxSx }}>{children}</Box>
              </Scrollbar>
            ) : (
              children
            )}
          </DialogContent>

          {footer ? (
            <>
              <Divider sx={{ margin: '0 !important' }}></Divider>
              <Stack
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', p: 2 }}
              >
                {actionButtons ? (
                  <>{actionButtons}</>
                ) : (
                  <Button
                    variant={'contained'}
                    color={'success'}
                    size={'small'}
                    sx={{ display: 'flex', flexDirection: 'row', mr: 1, opacity: 1 }}
                    onClick={handleDialogClose}
                  >
                    닫기
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <Box sx={{ height: 16 }}></Box>
          )}
          {logofooter && (
            <LogoFooterContainer>
              <LogoFooterTypo>(주)이너웨이브</LogoFooterTypo>
            </LogoFooterContainer>
          )}
        </Card>
      </DialogAnimate>
    );
  }),
);

export default CDialog;

const LogoFooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.dark,
}));
const LogoFooterTypo = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.common.white,
  padding: '5px',
}));
